// Modified by AI on 06/24/2026. Edit #1.
import {
    chain, findIndex, get, isEmpty, isEqual, keys as _keys, omit,
} from 'lodash';

import React, {
    useContext, useCallback, useReducer, useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { matchPath } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import UserHttpService from '../api/UserHttpService';
import { CONTACT_MODES } from '../constants/CandidateContactModes';
import LINKEDIN_CONNECTION_STATUSES from '../constants/LinkedinConnectionStatuses';
import { getFormattedContactChannelDate } from '../modules/candidate/profile/section-contact-channel-icons/ContactChannelUtils';
import Logger from '../services/logger';
import Talent from '../models/Talent';
import AuthorizationContext from './AuthorizationContext';
import LoadingContext, { LOADING_AREAS } from './LoadingContext';
import EmployerContext from './EmployerContext';
import NotificationContext from './NotificationContext';
import PositionContext from './PositionContext';
import CandidateService from '../api/CandidateProfileHttpService';
import EmployerWorkflowStatus from '../constants/EmployerWorkflowStatus';
import CandidatesStoreContext from './CandidatesStoreContext';
import ChromeExtensionService from '../services/ChromeExtensionService';
import { QUERY_KEYS, FIVE_MINUTES_MS } from '../constants/queryKeys';

const UPDATE_LINKEDIN_STATUSES_INTERVAL = 180000;

const {
    CONNECTED, INVITED, NOT_AVAILABLE, LOADING, NOT_CONNECTED,
} = LINKEDIN_CONNECTION_STATUSES;

const { DECLINED, FEEDBACK } = EmployerWorkflowStatus;

export const SET_LINKEDIN_CONTACT_DATA = 'LinkedinFormContainer/SET_LINKEDIN_CONTACT_DATA';
export const TRIGGER_CONTACT_DATA_RESET = 'LinkedinFormContainer/TRIGGER_CONTACT_DATA_RESET';
export const SET_CANDIDATES_CONTACT_DATA = 'LinkedinFormContainer/SET_CANDIDATES_CONTACT_DATA';
export const SET_CANDIDATES_LI_STATUSES = 'LinkedinFormContainer/SET_CANDIDATES_LI_STATUSES';
export const SET_IS_VALIDATIONS_LOADED = 'LinkedinFormContainer/SET_IS_VALIDATIONS_LOADED';
export const SET_IS_LINKEDIN_URL_VALID = 'LinkedinFormContainer/SET_IS_LINKEDIN_URL_VALID';
export const SET_IS_URL_BLOCKED = 'LinkedinFormContainer/SET_IS_URL_BLOCKED';
export const RESET_LI_STATUSES = 'LinkedinFormContainer/RESET_LI_STATUSES';

export const checkIsLinkedinUrlValid = (linkedinUrl) => linkedinUrl.search(/linkedin\.com\/in\/[^/]{3,}\/?/) !== -1;

const liStatuses = {
    LOADING: 0,
    NOT_AVAILABLE: 1,
    NOT_CONNECTED: 2,
    INVITED: 3,
    CONNECTED: 4,
};

let intervalId = null;

const getCandidateLinkedinId = (linkedinUrl) => chain(linkedinUrl)
    .trim('/ ')
    .split('/')
    .last()
    .value();

const sortTalentsByLinkedinStatusHelper = (candidatesContactData, candidatesLinkedinStatuses) => {
    const havingContact = candidatesContactData.filter((candidate) => Talent.getValidatedPreferredLinkedinAddress(candidate));

    const invited = Object.entries(candidatesLinkedinStatuses).filter(([k, v]) => (
        havingContact.map((c) => c.talentId).includes(k) && v.status === INVITED
    ));

    const connected = Object.entries(candidatesLinkedinStatuses).filter(([k, v]) => (
        havingContact.map((c) => c.talentId).includes(k) && v.status === CONNECTED
    ));

    const notConnected = havingContact.filter((candidate) => (
        candidatesLinkedinStatuses[candidate.talentId]?.status !== INVITED
                && candidatesLinkedinStatuses[candidate.talentId]?.status !== CONNECTED
    ));

    return {
        havingContact,
        invited,
        connected,
        notConnected,
    };
};

const liStatusContextInitialState = {
    candidatesContactData: [],
    havingContact: [],
    invited: [],
    connected: [],
    notConnected: [],
    mode: CONTACT_MODES.SINGLE,
    talentIds: [],
    isSingleMode: true,
    isBulkMode: false,
    candidatesLinkedinStatuses: {},
    isValidationsLoaded: false,
    isLinkedinUrlValid: true,
    isUrlBlocked: false,
};

const liStatusContextReducer = (state, action) => {
    switch (action.type) {
        case TRIGGER_CONTACT_DATA_RESET: {
            const areSameTalents = isEqual(state.talentIds, action.talentIds);
            const isSameMode = state.mode === action.mode;

            if (areSameTalents && isSameMode) {
                return state;
            }

            return {
                ...state,
                mode: action.mode,
                talentIds: action.talentIds,
                isSingleMode: action.mode !== CONTACT_MODES.BULK,
                isBulkMode: action.mode === CONTACT_MODES.BULK,
            };
        }
        case SET_IS_URL_BLOCKED:
            return {
                ...state,
                isUrlBlocked: action.isUrlBlocked,
            };
        case SET_IS_VALIDATIONS_LOADED:
            return {
                ...state,
                isValidationsLoaded: action.isValidationsLoaded,
            };
        case RESET_LI_STATUSES: {
            const newLiStatuses = omit(state.candidatesLinkedinStatuses, action.talentIds);

            return {
                ...state,
                candidatesLinkedinStatuses: newLiStatuses,
            };
        }
        case SET_CANDIDATES_LI_STATUSES: {
            const newLiStatuses = {
                ...state.candidatesLinkedinStatuses,
                ...action.candidatesLinkedinStatuses,
            };

            const {
                havingContact,
                invited,
                connected,
                notConnected,
            } = sortTalentsByLinkedinStatusHelper(
                state.candidatesContactData,
                newLiStatuses,
            );

            return {
                ...state,
                candidatesLinkedinStatuses: newLiStatuses,
                havingContact,
                invited,
                connected,
                notConnected,
            };
        }
        case SET_CANDIDATES_CONTACT_DATA: {
            const {
                havingContact,
                invited,
                connected,
                notConnected,
            } = sortTalentsByLinkedinStatusHelper(
                action.candidatesContactData,
                state.candidatesLinkedinStatuses,
            );

            return {
                ...state,
                candidatesContactData: action.candidatesContactData,
                havingContact,
                invited,
                connected,
                notConnected,
            };
        }
        default:
            return state;
    }
};

const CandidatesLinkedinStatusContext = React.createContext({
    linkedinConnectionsEnabled: false,
    linkedinEngagementEnabled: false,
    liStatusContextState: {},
    candidatesLinkedinStatuses: {},
    isBulkActionsContactByLinkedInEnabled: false,
    loadLinkedinStatuses: (candidates) => {},
    fetchCandidatesContactData: () => {},
    dispatchLiStatusContextAction: () => {},
});

// Modified by AI on 06/24/2026. Edit #2.
export const CandidatesLinkedinStatusApp = ({ children }) => {
    const location = useLocation();
    const queryClient = useQueryClient();

    const match = matchPath(location.pathname, {
        path: '*/workflowStep/:workflowStep/*',
        exact: false,
        strict: false,
    });

    const workflowStep = get(match, 'params.workflowStep', null);

    const { employer } = useContext(EmployerContext);
    const { userLinkedinId } = useContext(AuthorizationContext);
    const { positionId } = useContext(PositionContext);

    const { showErrorNotification } = useContext(NotificationContext);
    const { startFetchingData, stopFetchingData } = useContext(LoadingContext);

    const {
        areLinkedinConnectionsEnabled = false,
        isBulkActionsContactByLinkedInEnabled = false,
    } = employer?.settings || {};

    const linkedinConnectionsEnabled = areLinkedinConnectionsEnabled && userLinkedinId;
    const linkedinEngagementEnabled = areLinkedinConnectionsEnabled;
    const isShowContactBtns = ![FEEDBACK, DECLINED].includes(workflowStep);

    const [
        liStatusContextState,
        dispatchLiStatusContextAction,
    ] = useReducer(
        liStatusContextReducer,
        liStatusContextInitialState,
    );

    const {
        candidatesLinkedinStatuses,
        talentIds,
        isBulkMode,
        isSingleMode,
    } = liStatusContextState;

    // Modified by AI on 07/22/2026. Edit #2.
    const { visibleCandidatesRef, visibleCandidateRef } = useContext(CandidatesStoreContext);

    // Modified by AI on 07/20/2026. Edit #1.
    const updateLinkedinStatuses = useCallback(async () => {
        const talIds = _keys(candidatesLinkedinStatuses);
        const candidates = visibleCandidatesRef.current.filter((c) => talIds.includes(c.talentId));
        if (candidates?.length) {
            loadLinkedinStatuses(candidates, true);
        }
    }, [candidatesLinkedinStatuses, loadLinkedinStatuses]);

    const wrapFunction = useCallback((functionToWrap, timeToWait) => {
        let candidatesStack = [];
        let timeoutId = null;

        return (candidates, isUpdate) => new Promise((resolve) => {
            candidates.forEach((candidate) => {
                if (candidate?.talentId && findIndex(candidatesStack, (o) => o.talentId === candidate?.talentId) === -1) {
                    candidatesStack.push(candidate);
                }
            });

            if (!timeoutId) {
                timeoutId = setTimeout(async () => {
                    const candidatesToFetch = [...candidatesStack];
                    candidatesStack = [];
                    const result = await functionToWrap(candidatesToFetch, isUpdate);
                    resolve(result);
                    timeoutId = null;
                }, timeToWait);
            }
        });
    }, []);

    const loadLinkedinStatusesImpl = useCallback(async (candidates, isUpdate) => {
        if (!areLinkedinConnectionsEnabled) {
            return;
        }

        try {
            const candidateLinkedinIds = {};
            for (const candidate of candidates) {
                const linkedinAddresses = Talent.getLinkedinAddresses(candidate, true);
                const linkedinAddressesFromValidatedData = Talent.getLinkedinAddressesFromValidatedData(candidate, true);

                const uniqLinkedinAddresses = chain(linkedinAddresses)
                    .concat(linkedinAddressesFromValidatedData)
                    .uniq()
                    .value();

                if (!isUpdate || !uniqLinkedinAddresses?.length) {
                    dispatchLiStatusContextAction({
                        type: SET_CANDIDATES_LI_STATUSES,
                        candidatesLinkedinStatuses: { [candidate.talentId]: { status: uniqLinkedinAddresses?.length ? LOADING : NOT_AVAILABLE } },
                    });
                }

                if (uniqLinkedinAddresses?.length) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const linkedinAddress of uniqLinkedinAddresses) {
                        const candidateLinkedinId = getCandidateLinkedinId(linkedinAddress);
                        candidateLinkedinIds[candidateLinkedinId] = candidate.talentId;
                    }
                }
            }

            const keys = _keys(candidateLinkedinIds);
            if (keys?.length) {
                UserHttpService.getLinkedinProfileStatuses({ candidateLinkedinIds: _keys(candidateLinkedinIds), userLinkedinId })
                // eslint-disable-next-line no-loop-func
                    .then((result) => {
                        if (result?.statuses) {
                            const constructedStatuses = {};
                            for (const liPubId of _keys(result?.statuses)) {
                                const status = result?.statuses[liPubId];
                                const talentId = candidateLinkedinIds[liPubId];
                                if (talentId) {
                                    const statusUpdate = { status: NOT_CONNECTED, liPublicId: liPubId };
                                    if (status?.invited || status?.connected) {
                                        statusUpdate.status = status?.connected ? CONNECTED : INVITED;
                                    }
                                    if (statusUpdate?.status !== NOT_CONNECTED) {
                                        const date = status?.timestamp;
                                        const timestamp = getFormattedContactChannelDate(date ?? new Date());
                                        statusUpdate.timestamp = timestamp;
                                    }
                                    const constructedStatus = constructedStatuses[talentId];
                                    if (constructedStatus === undefined
                                        || ((liStatuses[statusUpdate.status] || 0) > (liStatuses[constructedStatus] || 0))
                                    ) {
                                        constructedStatuses[talentId] = statusUpdate.status;
                                        dispatchLiStatusContextAction({
                                            type: SET_CANDIDATES_LI_STATUSES,
                                            candidatesLinkedinStatuses: { [talentId]: statusUpdate },
                                        });
                                    }
                                }
                            }
                            return;
                        }

                        for (const liPublicId of _keys(candidateLinkedinIds)) {
                            const talentId = candidateLinkedinIds[liPublicId];
                            dispatchLiStatusContextAction({
                                type: SET_CANDIDATES_LI_STATUSES,
                                candidatesLinkedinStatuses: { [talentId]: { status: NOT_CONNECTED, liPublicId } },
                            });
                        }
                    });
            }
        } catch (err) {
            Logger.error(err);
        }
    }, [userLinkedinId, areLinkedinConnectionsEnabled]);

    const loadLinkedinStatuses = useCallback(wrapFunction(loadLinkedinStatusesImpl, 1500), [loadLinkedinStatusesImpl]);

    // Modified by AI on 06/24/2026. Edit #3.
    const fetchCandidatesContactData = useCallback(async (isUpdate) => {
        if (isBulkMode && !isShowContactBtns) {
            return;
        }

        try {
            startFetchingData(LOADING_AREAS.contactCandidateCandidatesEmailsData);

            const talentIdsKey = chain(talentIds).compact().sortBy().join(',')
                .value();

            const queryKey = QUERY_KEYS.candidatesContactData(positionId, talentIdsKey);

            // Modified by AI on 07/02/2026. Edit #1.
            // When isUpdate is true (explicit refresh), invalidate cache to force refetch
            if (isUpdate) {
                await queryClient.invalidateQueries({ queryKey, exact: true });
            }

            const contactDataRaw = await queryClient.fetchQuery({
                queryKey,
                queryFn: () => CandidateService.getCandidatesContactData({ positionId, talentIds }),
                staleTime: FIVE_MINUTES_MS,
                gcTime: FIVE_MINUTES_MS,
            });

            // Guard against null - BackendConnector.post() returns null when API result is null
            const contactData = contactDataRaw || [];

            dispatchLiStatusContextAction({ type: SET_CANDIDATES_CONTACT_DATA, candidatesContactData: contactData });

            if (!linkedinConnectionsEnabled) {
                return;
            }
            const needToLoadCandidates = [];
            for (const candidate of contactData) {
                const connData = candidatesLinkedinStatuses[candidate?.talentId];
                if (!connData || (isUpdate && (connData?.status !== CONNECTED))) {
                    needToLoadCandidates.push(candidate);
                }
            }
            if (needToLoadCandidates?.length) {
                await loadLinkedinStatuses(needToLoadCandidates, isUpdate);
            }
        } catch (error) {
            Logger.error(error);
            showErrorNotification();
        } finally {
            stopFetchingData(LOADING_AREAS.contactCandidateCandidatesEmailsData);
        }
    }, [
        isBulkMode,
        positionId,
        candidatesLinkedinStatuses,
        linkedinConnectionsEnabled,
        talentIds,
        isShowContactBtns,
        startFetchingData,
        showErrorNotification,
        stopFetchingData,
        queryClient,
    ]);

    const checkRecipientUrl = useCallback(async () => {
        if (isBulkMode) {
            return;
        }

        const talentId = talentIds[0];
        let candidate = visibleCandidatesRef.current.find((c) => c.talentId === talentId);

        if (!candidate) {
            candidate = visibleCandidateRef.current;
        }

        if (!candidate) {
            return;
        }

        const linkedinUrl = Talent.getValidatedPreferredLinkedinAddress(candidate);
        const isUrlValid = checkIsLinkedinUrlValid(linkedinUrl);

        dispatchLiStatusContextAction({ type: SET_IS_LINKEDIN_URL_VALID, isLinkedinUrlValid: isUrlValid });

        if (!isUrlValid) {
            return;
        }

        try {
            const linkedinConnectStatus = await ChromeExtensionService.getLinkedinCandidateConnectStatus({ profileUrl: linkedinUrl });

            dispatchLiStatusContextAction({ type: SET_IS_URL_BLOCKED, isUrlBlocked: !linkedinConnectStatus });
        } catch (error) {
            Logger.error(error);
            dispatchLiStatusContextAction({ type: SET_IS_URL_BLOCKED, isUrlBlocked: true });
        }
    }, [talentIds, isBulkMode]);

    const validateRecipient = useCallback(async () => {
        dispatchLiStatusContextAction({ type: SET_IS_VALIDATIONS_LOADED, isValidationsLoaded: false });

        if (window.ChromeExtensionId && userLinkedinId && isSingleMode) {
            await checkRecipientUrl();
        }

        dispatchLiStatusContextAction({ type: SET_IS_VALIDATIONS_LOADED, isValidationsLoaded: true });
    }, [userLinkedinId, isSingleMode, checkRecipientUrl]);

    useEffect(() => {
        if (!talentIds || isEmpty(talentIds)) {
            return;
        }

        fetchCandidatesContactData();
    }, [talentIds]);

    useEffect(() => {
        if (!talentIds || isEmpty(talentIds)) {
            return;
        }

        validateRecipient();
    }, [talentIds]);

    useEffect(() => {
        clearInterval(intervalId);
        intervalId = setInterval(updateLinkedinStatuses, UPDATE_LINKEDIN_STATUSES_INTERVAL);
        return () => clearInterval(intervalId);
    }, [updateLinkedinStatuses]);

    return (
        <CandidatesLinkedinStatusContext.Provider value={{
            linkedinConnectionsEnabled,
            linkedinEngagementEnabled,
            isBulkActionsContactByLinkedInEnabled,
            candidatesLinkedinStatuses,
            liStatusContextState,
            loadLinkedinStatuses,
            fetchCandidatesContactData,
            dispatchLiStatusContextAction,
        }}>
            {children}
        </CandidatesLinkedinStatusContext.Provider>
    );
};

export default CandidatesLinkedinStatusContext;
